import {FunctionsPreviewResponse} from '@packages/domains/code-libraries/data-types';
import {buildFunctionsPreviewKey} from '@packages/systems/code-modules/constants/functions';
import {engineConfig, type Dispatch} from '@packages/systems/flux';
import {getFeatureFlagOutsideOfReact} from '@packages/systems/feature-config/client';
import {FEATURE_FLAGS} from '@packages/systems/feature-config/shared';
import fetch from '@packages/utilities/fetch';
import {once} from '@packages/utilities/memo';
import {selectFunctionsSandboxEngineState} from './CodeLibrariesSlice';
import {
  functionsPreviewConnected,
  functionsPreviewConnecting,
  functionsPreviewDisabled,
  functionsPreviewFailed,
  functionsPreviewEmpty,
  functionsGenerationMissing,
  type FunctionsSandboxActions,
} from './actionCreators';
import type {FunctionsCmsBroker} from './functionsCmsBroker';
import type {
  FunctionsPreview,
  FunctionsSandboxEngineState,
  FunctionsSandboxState,
} from './types';

/**
 * How long to wait after each failed preview request before trying again.
 * After the last delay, we stop retrying.
 */
const BACKOFF_MS = [5_000, 15_000, 30_000, 60_000] as const;

/**
 * How long a preview POST may run before we cancel it and retry.
 * webflowFetch has no timeout; without this, a hung request leaves
 * inFlight set and CONNECTING never reaches the retry path.
 */
const REQUEST_TIMEOUT_MS = 60_000;

function nextBackoff(attempt: number): number | null {
  const delay = BACKOFF_MS[attempt - 1];
  return delay == null ? null : Date.now() + delay;
}

function isRefreshDue(preview: FunctionsPreview): boolean {
  return Date.now() >= preview.refreshAt;
}

function isAbortError(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'name' in error &&
    (error as {name: unknown}).name === 'AbortError'
  );
}

type PreviewHttpResponse = {
  ok: boolean;
  status: number;
  json: () => Promise<unknown>;
};

type PreviewHttpResult =
  | {kind: 'disabled'}
  | {kind: 'failed'; retryable: boolean}
  | {kind: 'invalid-body'}
  | {kind: 'no-functions'}
  | {kind: 'ready'; preview: FunctionsPreview; generationMissing: boolean};

async function parsePreviewResponse(
  response: PreviewHttpResponse
): Promise<PreviewHttpResult> {
  if (response.status === 403) {
    return {kind: 'disabled'};
  }
  if (!response.ok) {
    return {
      kind: 'failed',
      retryable: response.status === 429 || response.status >= 500,
    };
  }
  let json: unknown;
  try {
    json = await response.json();
  } catch {
    return {kind: 'invalid-body'};
  }
  let parsed: FunctionsPreviewResponse;
  try {
    parsed = FunctionsPreviewResponse.parse(json);
  } catch {
    return {kind: 'invalid-body'};
  }
  if (parsed.status === 'no-functions') {
    return {kind: 'no-functions'};
  }
  return {
    kind: 'ready',
    preview: {
      origin: parsed.previewOrigin,
      token: parsed.previewToken,
      refreshAt: parsed.refreshAt.getTime(),
      tokenExpiresAt: parsed.tokenExpiresAt.getTime(),
    },
    generationMissing: parsed.siteFunctionsGeneration === 'missing',
  };
}

/**
 * Keeps a live Functions preview sandbox for the current site and modules.
 *
 * Only this engine writes the sandbox store. It talks to the server in one
 * place: when status is CONNECTING and no request is already running, it
 * POSTs `/functions/preview` and sets the next status from the response.
 *
 * Everything else — first connect, switching modules, refreshing credentials,
 * keep-alive, retry — just sets status to CONNECTING so that POST runs again.
 * If the modules change while a request is still going, that request is cancelled.
 *
 * Statuses:
 * - IDLE: no preview yet. Once we know the site and at least one module,
 *   we start connecting.
 * - CONNECTING: requesting a preview. Success → READY (or NO_FUNCTIONS if
 *   these modules have no functions). 403 or the feature flag off → DISABLED.
 *   Retryable errors → FAILED. Callers can keep using the previous preview
 *   until the new one is ready, as long as it's for the same module generation.
 * - READY: the sandbox is usable. We reconnect at `refreshAt` — from a timer,
 *   or when the user comes back to the tab.
 * - FAILED: waiting to retry, using BACKOFF_MS. After the last delay we stay
 *   FAILED (`retryAt` is null). The last preview stays on the store while we
 *   wait, reusable only for the same module generation.
 * - NO_FUNCTIONS: these modules have no functions. We stay here until the
 *   set changes, or becomes empty (treated the same way).
 * - DISABLED: feature flag off or 403. We stay here for the rest of this
 *   Designer session.
 */
export const FunctionsSandboxEngine = (
  dispatch: Dispatch<FunctionsSandboxActions>
) => {
  /** Preview request currently running, and which modules it was for. */
  let inFlight: {
    request: ReturnType<typeof fetch>;
    key: string;
  } | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let requestTimer: ReturnType<typeof setTimeout> | null = null;
  let attempts = 0;
  let currentKey = '';
  let currentStatus: FunctionsSandboxState['status'] = 'IDLE';
  let currentPreview: FunctionsPreview | undefined;
  let cmsBroker: Promise<FunctionsCmsBroker | undefined> | undefined;
  let lastMissingHash: string | undefined;

  const clearTimer = () => {
    if (timer != null) {
      clearTimeout(timer);
      timer = null;
    }
  };

  const clearRequestTimer = () => {
    if (requestTimer != null) {
      clearTimeout(requestTimer);
      requestTimer = null;
    }
  };

  const abortInFlightRequest = () => {
    clearRequestTimer();
    if (inFlight) {
      inFlight.request.cancel();
      inFlight = null;
    }
  };

  /**
   * Ask for a preview: set status to CONNECTING so the next engine tick sends a request.
   * Clears any waiting timer first, so a keep-alive or retry cannot fire
   * after we've already started connecting.
   */
  const connectToSandbox = (moduleKey: string) => {
    clearTimer();
    dispatch(functionsPreviewConnecting({moduleKey}));
  };

  /**
   * Connect at a given time. When READY, that's just before the sandbox
   * would go idle (`refreshAt`). When FAILED, that's the next retry.
   * Does nothing if a timer is already set — Flux can tick READY twice
   * with the same preview.
   */
  const scheduleConnectAt = (at: number, moduleKey: string) => {
    if (timer != null) {
      return;
    }
    timer = setTimeout(
      () => {
        connectToSandbox(moduleKey);
      },
      Math.max(at - Date.now(), 0)
    );
  };

  /**
   * Browsers slow down timers in background tabs, so keep-alive can miss
   * `refreshAt`. Coming back to the tab does not re-run the engine, so we
   * listen here and reconnect if the preview is already due.
   */
  const onVisibilityChange = () => {
    if (document.visibilityState !== 'visible') {
      return;
    }
    if (
      currentStatus === 'READY' &&
      currentKey &&
      currentPreview &&
      isRefreshDue(currentPreview)
    ) {
      connectToSandbox(currentKey);
    }
  };

  /** Attach the tab-visible listener once, the first time we have work to do. */
  const listenForVisibilityChange = once(() => {
    document.addEventListener('visibilitychange', onVisibilityChange);
  });

  const handleFailedRequest = ({
    moduleKey,
    retryable,
  }: {
    moduleKey: string;
    retryable: boolean;
  }) => {
    if (!retryable) {
      dispatch(
        functionsPreviewFailed({
          moduleKey,
          attempts: attempts + 1,
          retryAt: null,
        })
      );
      return;
    }

    // Remember the failure. If we can retry, the FAILED tick waits, then
    // connects again. If the delays are used up, retryAt is null and we stop.
    attempts += 1;
    const retryAt = nextBackoff(attempts);
    dispatch(functionsPreviewFailed({moduleKey, attempts, retryAt}));
  };

  /**
   * Start the preview POST now, so `inFlight` is set before this tick
   * finishes. Returns an async function that waits for the body and
   * updates the store — call it, but do not await it, or the tick hangs.
   */
  const makePreviewRequest = (
    siteShortName: string,
    moduleIds: string[],
    siteFunctionsHash: string | undefined,
    moduleKey: string
  ) => {
    const request = fetch(
      `/api/code-libraries/${siteShortName}/functions/preview`,
      {
        method: 'POST',
        body: JSON.stringify({moduleIds, siteFunctionsHash}),
      }
    );
    const pending = {request, key: moduleKey};
    inFlight = pending;

    const timeout = setTimeout(() => {
      if (inFlight !== pending) {
        return;
      }
      abortInFlightRequest();
      handleFailedRequest({moduleKey, retryable: true});
    }, REQUEST_TIMEOUT_MS);
    requestTimer = timeout;

    return async () => {
      try {
        const response = await request;
        // Ignore the result if this request was cancelled or replaced.
        if (inFlight !== pending) {
          return;
        }
        const result = await parsePreviewResponse(response);
        if (inFlight !== pending) {
          return;
        }
        switch (result.kind) {
          case 'disabled':
            dispatch(functionsPreviewDisabled());
            return;
          case 'failed':
            handleFailedRequest({moduleKey, retryable: result.retryable});
            return;
          case 'invalid-body':
            handleFailedRequest({moduleKey, retryable: false});
            return;
          case 'no-functions':
            attempts = 0;
            dispatch(functionsPreviewEmpty({moduleKey}));
            return;
          case 'ready':
            attempts = 0;
            if (!result.generationMissing) {
              lastMissingHash = undefined;
            } else if (
              // A repeat miss means the catalog is not retaining this hash, so
              // serve the newest bundle instead of rebuilding in a loop.
              siteFunctionsHash &&
              siteFunctionsHash !== lastMissingHash
            ) {
              lastMissingHash = siteFunctionsHash;
              dispatch(
                functionsGenerationMissing({functionsHash: siteFunctionsHash})
              );
              return;
            }
            dispatch(
              functionsPreviewConnected({
                moduleKey,
                preview: result.preview,
              })
            );
        }
      } catch (error) {
        if (inFlight !== pending || isAbortError(error)) {
          return;
        }
        handleFailedRequest({moduleKey, retryable: true});
      } finally {
        clearTimeout(timeout);
        if (requestTimer === timeout) {
          requestTimer = null;
        }
        if (inFlight === pending) {
          inFlight = null;
        }
      }
    };
  };

  return ({
    sandboxState,
    siteShortName,
    moduleIds,
  }: FunctionsSandboxEngineState) => {
    currentStatus = sandboxState.status;
    currentPreview =
      'preview' in sandboxState ? sandboxState.preview : undefined;

    // The build replaces NODE_ENV, so every other build drops this branch and
    // never emits the CMS broker's chunk.
    if (process.env.NODE_ENV === 'development') {
      const target =
        currentPreview && siteShortName
          ? {
              origin: currentPreview.origin,
              token: currentPreview.token,
              siteShortName,
            }
          : null;
      if (target) {
        cmsBroker ??= import('./functionsCmsBroker')
          .then(({createFunctionsCmsBroker}) => createFunctionsCmsBroker())
          .catch(() => {
            // A failed load (the dev bundle rebuilding, say) is retried on
            // the next run instead of being cached.
            cmsBroker = undefined;
            return undefined;
          });
      }
      cmsBroker?.then((broker) => broker?.setTarget(target));
    }

    if (!getFeatureFlagOutsideOfReact(FEATURE_FLAGS.CODE_FUNCTIONS)) {
      if (sandboxState.status !== 'DISABLED') {
        dispatch(functionsPreviewDisabled());
      }
      return;
    }

    if (!siteShortName) {
      return;
    }

    const moduleKey = buildFunctionsPreviewKey(
      moduleIds,
      sandboxState.siteFunctionsHash
    );
    currentKey = moduleKey;

    if (inFlight && inFlight.key !== moduleKey) {
      // These modules are no longer selected; drop the old request.
      abortInFlightRequest();
    }

    if (moduleIds.length === 0) {
      // Nothing to preview. Cancel work and park on NO_FUNCTIONS.
      abortInFlightRequest();
      clearTimer();
      attempts = 0;
      if (
        sandboxState.status !== 'NO_FUNCTIONS' ||
        sandboxState.moduleKey !== moduleKey
      ) {
        dispatch(functionsPreviewEmpty({moduleKey}));
      }
      return;
    }

    listenForVisibilityChange();

    // The only place we POST. Other paths set CONNECTING so we get here.
    if (sandboxState.status === 'CONNECTING' && !inFlight) {
      if (sandboxState.moduleKey !== moduleKey) {
        // Store is still connecting for the previous modules.
        connectToSandbox(moduleKey);
        return;
      }
      const requestPreview = makePreviewRequest(
        siteShortName,
        moduleIds,
        sandboxState.siteFunctionsHash,
        moduleKey
      );
      // Do not await this or the engine will hang
      requestPreview();
      return;
    }

    if (
      // Already POSTing — wait for it.
      sandboxState.status === 'CONNECTING' ||
      // A rebuild is pending; the store moves to IDLE when it finishes.
      sandboxState.status === 'AWAITING_REBUILD' ||
      // Flag off or 403: stay here for the rest of the session.
      sandboxState.status === 'DISABLED'
    ) {
      return;
    }

    const stateKey =
      'moduleKey' in sandboxState ? sandboxState.moduleKey : undefined;
    const keyChanged = stateKey !== moduleKey;

    if (keyChanged) {
      // Different modules than the store; start a new preview.
      attempts = 0;
      connectToSandbox(moduleKey);
      return;
    }

    if (sandboxState.status === 'IDLE') {
      connectToSandbox(moduleKey);
      return;
    }

    if (sandboxState.status === 'NO_FUNCTIONS') {
      return;
    }

    if (sandboxState.status === 'FAILED') {
      if (sandboxState.retryAt == null) {
        // Delays used up; don't retry again.
        return;
      }
      if (Date.now() < sandboxState.retryAt) {
        scheduleConnectAt(sandboxState.retryAt, moduleKey);
        return;
      }
      connectToSandbox(moduleKey);
      return;
    }

    if (sandboxState.status === 'READY') {
      if (isRefreshDue(sandboxState.preview)) {
        connectToSandbox(moduleKey);
        return;
      }
      scheduleConnectAt(sandboxState.preview.refreshAt, moduleKey);
    }
  };
};

export const functionsSandboxEngineConfig = engineConfig(
  FunctionsSandboxEngine,
  selectFunctionsSandboxEngineState
);
